/**
 * The parse-failure tail of `runPlayerInput` (split at the 500-line cap) —
 * what an `unrecognized` input becomes once its prefix has run: an authored
 * reaction (D27), or the teach (D24/D38), each followed by its occurrence
 * listeners (R12) and, mid-chain, the §8.8 abandonment coda.
 *
 * @packageDocumentation
 */

import type { NarrativeLine } from '@orpheus/content-schema';

import { foldCharacters, type ParseDiagnostics, type TokenCorrection } from '@orpheus/parser-engine';

import type { EngineGameBundle } from '../bundle/engine-game-bundle.js';

import { bumpResponseUsage, parseTeachResponse, type SpokenResponse } from '../defaults/default-responses.js';
import { respondToInputOccurrence } from '../narrative/occurrences.js';
import { type ClauseWalk, speakChainAbandoned } from './clause-walk.js';
import { type PlayerInputOutcome } from './process-player-input.js';
import { appendTranscript, inputNamespaceCursor, stampNarrativeLines } from './transcript-cursor.js';

/**
 * Finish an unrecognized input whose prefix walk completed without a refusal.
 *
 * @param bundle - The loaded bundle
 * @param walk - The completed prefix walk (echo + prefix entries committed)
 * @param diagnostics - Why the failing clause could not parse (R21 narrowed this from the whole unrecognized arm — the diagnostics are all this tail reads)
 * @param rawInput - Exactly what the player typed (reactions key on THIS — D45)
 * @param corrections - The input's aggregate fuzzy fixes
 * @param spokenTeachOverride - The in-context refusal for a failed ANSWER (D38), when the caller has one
 * @param inputOrdinal - This input's id namespace
 * @param remainder - Clause texts the parser never reached (§8.8's coda)
 * @returns The reaction or parse_failed outcome, fully committed
 */
export const finishParseFailure = (
	bundle: EngineGameBundle,
	walk: ClauseWalk,
	diagnostics: ParseDiagnostics,
	rawInput: string,
	corrections: readonly TokenCorrection[],
	spokenTeachOverride: SpokenResponse | undefined,
	inputOrdinal: number,
	remainder: readonly string[],
): PlayerInputOutcome => {
	// D27: an input the parser cannot place may be an authored reaction — checked ONLY
	// here, on the failure path, so an egg can never shadow a real command.
	//
	// D45: keyed on what the player TYPED, never on the splice. A reaction is
	// an EXACT-INPUT response by definition, and while a question was parked
	// this read the splice — so "plugh" became "open the door with plugh"
	// and every authored egg in the pack was unreachable for the duration.
	const reaction = authoredReactionFor(bundle, reactionKeyOf(rawInput));

	if (reaction !== undefined) {
		return finishWithReaction(bundle, walk, reaction, corrections, inputOrdinal, remainder);
	}

	// D38: an ANSWER that resolves to nothing is refused IN CONTEXT — the
	// caller supplies the spoken response, because only it knows what was
	// asked. D58: the override carries its OWN slot, so the count that
	// advances is the count of what actually spoke — the old line-only
	// override bumped the unspoken teach slot's pool instead, a miscount
	// nothing could observe until every slot became poolable.
	const spokenTeach = spokenTeachOverride ?? parseTeachResponse(bundle, diagnostics, walk.state.session.responseUsage);
	const teach = stampNarrativeLines(
		bundle,
		walk.state,
		inputNamespaceCursor(inputOrdinal, walk.inputSequence, walk.state),
		[spokenTeach.line],
		'parse_teach',
	);
	// D30: the slot's fire count commits WITH the line — sequential pools
	// replay byte-exact because the count lives in RuntimeState.
	const taughtState = appendTranscript(walk.state, teach.entries);
	// R12: a failed parse is an occurrence — the engine teaches first, then
	// the listeners get their word (the character comments AFTER the teach).
	const responded = respondToInputOccurrence(
		bundle,
		{
			...taughtState,
			session: {
				...taughtState.session,
				responseUsage: bumpResponseUsage(taughtState.session.responseUsage, [spokenTeach.slot]),
			},
		},
		inputOrdinal,
		walk.inputSequence + teach.entries.length,
		{ detail: diagnostics.failureKind, family: 'parse_failed' },
	);

	// §8.8: the failed clause's tail is abandoned; the teach speaks first,
	// the abandonment is the input's coda.
	const taughtWalk = speakAbandonmentCoda(
		bundle,
		{
			...walk,
			entries: [...walk.entries, ...teach.entries, ...responded.entries],
			inputSequence: walk.inputSequence + teach.entries.length + responded.entries.length,
			state: responded.state,
		},
		inputOrdinal,
		remainder,
	);

	return {
		corrections,
		diagnostics,
		nextState: taughtWalk.state,
		transcriptEntries: taughtWalk.entries,
		type: 'parse_failed',
	};
};

/** One authored reaction, found: the key it was found under and its line. */
export interface AuthoredReaction {
	readonly key: string;
	readonly line: NarrativeLine;
}

/**
 * The pack's reaction for a lookup key, if it authored one (D27).
 *
 * @remarks
 * Keys are compared folded (D226, E13): a key an author wrote with an accent
 * meets the player's unaccented typing, and the schema's lowercase rule
 * stands as it was.
 *
 * @param bundle - The loaded bundle
 * @param key - The normalized lookup key ({@link reactionKeyOf})
 * @returns The reaction, or `undefined` when the pack authored none for it
 */
export const authoredReactionFor = (bundle: EngineGameBundle, key: string): AuthoredReaction | undefined => {
	const reactions = bundle.contentPack.reactions ?? {};
	const direct = reactions[key];

	if (direct !== undefined) {
		return { key, line: direct };
	}

	const folded = Object.entries(reactions).find(([authoredKey]) => reactionKeyOf(authoredKey) === key);

	return folded === undefined ? undefined : { key: folded[0], line: folded[1] };
};

/**
 * Finish an input with an authored reaction (D27): the egg line committed,
 * the `reaction_spoken` occurrence tallied and listened for, the §8.8 coda
 * for any clauses behind it.
 *
 * @remarks
 * Two paths end here. A whole-input parse failure that matches a key — the
 * D27 case — and, since D226 (E6), a clause that parsed as a declared-
 * unsupported verb standing alone (`listen`, `smell`) when the pack wrote a
 * reaction under that very word. D27's law is that a reaction never shadows
 * a real command; an unsupported verb with nothing to act on is not one —
 * the roster would refuse it from a slot — so the authored line is the
 * better answer, and the occurrence it raises is the one the pack can listen
 * for.
 *
 * @param bundle - The loaded bundle
 * @param walk - The walk so far (echo + any prefix committed)
 * @param reaction - The reaction found for the input
 * @param corrections - The input's aggregate fuzzy fixes
 * @param inputOrdinal - This input's id namespace
 * @param remainder - Clause texts that will not run (§8.8's coda)
 * @returns The reaction outcome, fully committed
 */
export const finishWithReaction = (
	bundle: EngineGameBundle,
	walk: ClauseWalk,
	reaction: AuthoredReaction,
	corrections: readonly TokenCorrection[],
	inputOrdinal: number,
	remainder: readonly string[],
): PlayerInputOutcome => {
	const stamped = stampNarrativeLines(
		bundle,
		walk.state,
		inputNamespaceCursor(inputOrdinal, walk.inputSequence, walk.state),
		[reaction.line],
		'reaction',
	);
	// R12: a spoken reaction is an occurrence — tallied, and listened for.
	const responded = respondToInputOccurrence(
		bundle,
		appendTranscript(walk.state, stamped.entries),
		inputOrdinal,
		walk.inputSequence + stamped.entries.length,
		{ detail: reaction.key, family: 'reaction_spoken' },
	);

	// §8.8: an egg mid-chain still abandoned the tail — the coda says so.
	const reactedWalk = speakAbandonmentCoda(
		bundle,
		{
			...walk,
			entries: [...walk.entries, ...stamped.entries, ...responded.entries],
			inputSequence: walk.inputSequence + stamped.entries.length + responded.entries.length,
			state: responded.state,
		},
		inputOrdinal,
		remainder,
	);

	return {
		corrections,
		nextState: reactedWalk.state,
		transcriptEntries: reactedWalk.entries,
		type: 'reaction',
	};
};

/** Speak the §8.8 coda when a parse-level stop abandoned clauses (no-op otherwise). */
const speakAbandonmentCoda = (
	bundle: EngineGameBundle,
	walk: ClauseWalk,
	inputOrdinal: number,
	remainder: readonly string[],
): ClauseWalk => (remainder.length === 0 ? walk : speakChainAbandoned(bundle, walk, inputOrdinal, remainder));

/**
 * The D27 reaction lookup key: character-folded (lowercase, typography
 * normalized), separator punctuation stripped, whitespace collapsed — so
 * "  DUDE!! " and "dude" are the same egg. Mirrors what the tokenizer's
 * separator split would discard; deterministic by construction.
 *
 * @param rawInput - Exactly what the player typed
 * @returns The normalized lookup key
 */
export const reactionKeyOf = (rawInput: string): string =>
	foldCharacters(rawInput)
		.replace(/[!,.:;?]+/gu, ' ')
		.replace(/\s+/gu, ' ')
		.trim();
